import type { ApiResponse } from "../types/dto";
import { attributionHeaders } from "../utils/trafficAttribution";

// Production leaves this empty so browser API calls stay on the website origin and pass through
// the Worker BFF. Local development can still set VITE_API_BASE_URL to an absolute API origin.
export const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

export class ApiError extends Error {
  readonly code: string | null | undefined;
  readonly status: number;
  readonly traceId: string;

  constructor(code: string | null | undefined, message: string, status: number, traceId: string) {
    super(message);
    this.code = code;
    this.status = status;
    this.traceId = traceId;
  }
}

// The website's own public endpoints (IP check, environment check, …) write the query log, and the query
// log attributes each round to the visit's first touch — which only the browser knows.
function isWebsiteApiPath(path: string): boolean {
  return path.startsWith("/api/public/");
}

async function request<T>(method: string, path: string, body?: unknown, extraHeaders?: Record<string, string>): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(isWebsiteApiPath(path) ? attributionHeaders() : {}),
      ...extraHeaders,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let parsed: ApiResponse<T> | null = null;
  try {
    parsed = (await response.json()) as ApiResponse<T>;
  } catch {
    // Non-JSON body (for example, an upstream 502) falls through to the generic error below.
  }

  if (!parsed || !parsed.success) {
    throw new ApiError(
      parsed?.code,
      parsed?.message ?? `Request failed with status ${response.status}.`,
      response.status,
      parsed?.traceId ?? "unknown",
    );
  }

  return parsed.data as T;
}

export const apiClient = {
  get: <T>(path: string, extraHeaders?: Record<string, string>) => request<T>("GET", path, undefined, extraHeaders),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
};
